import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileCheck2, Paperclip } from "lucide-react";
import { Card } from "@shared/ui/containers/Card";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { pageCopy } from "@shared/lib/pageCopy";
import { fetchMyResponseDetail, type SubmissionAnswer } from "./api";
import { formTypeLabel, getSurveyRouteContext } from "./routeContext";

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "long",
  year: "numeric",
});

export function SurveyResponseView(): JSX.Element {
  const location = useLocation();
  const { responseId = "" } = useParams<{ responseId: string }>();
  const copy = pageCopy().surveys;
  const route = getSurveyRouteContext(location.pathname);
  // Return to wherever the member opened this from (e.g. the responses list),
  // falling back to the list route when navigated to directly.
  const navState = location.state as { backTo?: string; backLabel?: string } | null;
  const backTo = navState?.backTo ?? route.listRoute;
  const backLabel = navState?.backLabel ?? route.detailBackLabel;

  const query = useQuery({
    queryKey: ["survey-response", responseId],
    queryFn: () => fetchMyResponseDetail(responseId),
    enabled: !!responseId,
  });

  const groupedAnswers = useMemo(
    () => groupAnswers(query.data?.answers ?? []),
    [query.data?.answers],
  );
  const response = query.data;
  const formLabel = formTypeLabel(response?.flow_type, route.isEformRoute);
  const header = (
    <HeroHeader
      eyebrow={copy.eyebrow}
      title={response?.name ?? copy.title}
      subtitle={
        response
          ? responseSubtitle(response.submitted_at, formLabel)
          : copy.subtitle
      }
      backTo={backTo}
      backLabel={backLabel}
      backBehavior="parent"
    />
  );

  if (query.isLoading) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 pb-safe-or-6">
          <ErrorState onRetry={() => query.refetch()} />
        </div>
      </div>
    );
  }

  const responseData = query.data;
  const resolvedFormLabel = formTypeLabel(responseData.flow_type, route.isEformRoute);

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={responseData.name}
        subtitle={responseSubtitle(
          responseData.submitted_at,
          resolvedFormLabel,
        )}
        backTo={backTo}
        backLabel={backLabel}
        backBehavior="parent"
      />

      <div className="px-5 pt-5 pb-safe-or-6">
        {responseData.submitted_at &&
        responseData.display_context?.completion_message?.html?.trim() ? (
          <Card noGrain className="p-4">
            <div
              className="onboarding-content text-left text-body text-ink-secondary [&_h1]:font-display [&_h1]:text-display-lg [&_h1]:text-ink [&_h2]:font-display [&_h2]:text-headline [&_h2]:text-ink [&_h3]:font-display [&_h3]:text-headline [&_h3]:text-ink [&_p]:mt-1.5 [&_a]:text-primary-700 [&_ul]:list-disc [&_ul]:pl-5"
              // Content is authored in the tenant's survey template (trusted admin input).
              dangerouslySetInnerHTML={{
                __html: responseData.display_context.completion_message.html,
              }}
            />
          </Card>
        ) : null}

        {groupedAnswers.length === 0 ? (
          <div className="mt-4">
            <EmptyState
              icon={<FileCheck2 className="h-7 w-7" strokeWidth={1.5} />}
              title="No captured answers."
              description="This response exists, but there are no visible answers to show yet."
            />
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-4">
            {groupedAnswers.map((group) => (
              <Card key={group.title} noGrain>
                <p className="mb-3 font-display text-headline text-ink">
                  {group.title}
                </p>
                <div className="flex flex-col divide-y divide-hairline">
                  {group.answers.map((answer, index) => (
                    <div
                      key={`${answer.content_key ?? answer.item_key ?? index}`}
                      className="py-3 first:pt-0 last:pb-0"
                    >
                      <p className="text-caption uppercase tracking-[0.14em] text-ink-muted">
                        {answer.name ??
                          answer.item_key ??
                          answer.content_key ??
                          `Answer ${index + 1}`}
                      </p>
                      <AnswerValue answer={answer} />
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function responseSubtitle(
  submittedAt: string | null | undefined,
  formLabel: string,
): string {
  if (!submittedAt) return `${formLabel} response in progress`;
  return `${formLabel} submitted ${DATE_FMT.format(new Date(submittedAt))}`;
}

function groupAnswers(
  answers: SubmissionAnswer[],
): Array<{ title: string; answers: SubmissionAnswer[] }> {
  const sorted = [...answers].sort((a, b) => {
    const stepDelta = (a.step_sequence ?? 0) - (b.step_sequence ?? 0);
    if (stepDelta !== 0) return stepDelta;
    return (a.sequence_number ?? 0) - (b.sequence_number ?? 0);
  });

  const groups = new Map<string, SubmissionAnswer[]>();
  sorted.forEach((answer) => {
    const title = answer.step_name || "Submitted answers";
    groups.set(title, [...(groups.get(title) ?? []), answer]);
  });

  return Array.from(groups.entries()).map(([title, group]) => ({
    title,
    answers: group,
  }));
}

/** Answers come back JSON-encoded (e.g. the string `"http://…"` or `["a","b"]`); unwrap once. */
function coerceAnswerValue(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  const first = trimmed[0];
  if (first === '"' || first === "[" || first === "{") {
    try {
      return JSON.parse(trimmed);
    } catch {
      /* keep original */
    }
  }
  return value;
}

function isUrl(value: unknown): value is string {
  return typeof value === "string" && /^https?:\/\//i.test(value);
}

function isImageUrl(url: string): boolean {
  const path = url.split("?")[0]?.toLowerCase() ?? "";
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(path);
}

/**
 * Render a submitted answer value: uploaded images as thumbnails (link to full size),
 * other uploaded files as a compact "View file" link, and everything else as text —
 * so long signed URLs are never dumped as raw text.
 */
function AnswerValue({ answer }: { answer: SubmissionAnswer }): JSX.Element {
  const value = coerceAnswerValue(answer.item_value);
  const urls: string[] = Array.isArray(value)
    ? (value as unknown[]).filter(isUrl)
    : isUrl(value)
      ? [value]
      : [];

  if (urls.length > 0) {
    return (
      <div className="mt-1 flex flex-wrap items-center gap-3">
        {urls.map((url, i) =>
          isImageUrl(url) ? (
            <a key={url + i} href={url} target="_blank" rel="noreferrer" className="block">
              <img
                src={url}
                alt={answer.name ?? "Attachment"}
                className="h-28 w-28 rounded-md object-cover hairline"
              />
            </a>
          ) : (
            <a
              key={url + i}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-body text-primary-700 underline"
            >
              <Paperclip className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              View file{urls.length > 1 ? ` ${i + 1}` : ""}
            </a>
          ),
        )}
      </div>
    );
  }

  return <p className="mt-1 whitespace-pre-line text-body text-ink">{formatAnswer(value)}</p>;
}

function formatAnswer(value: unknown): string {
  if (value === null || value === undefined || value === "")
    return "Not answered";
  if (Array.isArray(value)) return value.map(formatAnswer).join(", ");
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}
